import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { 
    CCard, CCardHeader, CCardBody, CButton, CSpinner, CRow, CCol, 
    CListGroup, CListGroupItem, CTable, CTableHead, CTableRow, 
    CTableHeaderCell, CTableBody, CTableDataCell, CFormInput, CFormCheck, CCollapse,
    CTooltip,CInputGroup
} from '@coreui/react';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from "react-datepicker";
import ru from 'date-fns/locale/ru';
import enUS from 'date-fns/locale/en-US';
import dayjs from 'dayjs';
import { ResponsivePie } from '@nivo/pie';
import { useTranslation } from 'react-i18next';
import CIcon from '@coreui/icons-react';
import { cilSettings, cilCheckCircle, cilXCircle } from '@coreui/icons';

registerLocale('ru', ru);
registerLocale('en', enUS);

// УНИВЕРСАЛЬНАЯ ФУНКЦИЯ конвертации локальной даты пользователя в правильный UTC для БД
const formatToCameraUTC = (localDate, offset) => {
    const y = localDate.getFullYear();
    const m = localDate.getMonth();
    const d = localDate.getDate();
    const h = localDate.getHours();
    const min = localDate.getMinutes();
    const s = localDate.getSeconds();
    return new Date(Date.UTC(y, m, d, h - 3 - offset, min, s)).toISOString();
};

const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '00:00:00';
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const MultiCameraAnalytics = ({ selectedCameras }) => {
    const { t, i18n } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [startDate, setStartDate] = useState(dayjs().startOf('day').toDate());
    const [endDate, setEndDate] = useState(dayjs().endOf('day').toDate());
    const [minPercentage, setMinPercentage] = useState(5);
    const [showConfig, setShowConfig] = useState(false);

    // ИСПРАВЛЕНИЕ: Теперь мульти-дашборд понимает все режимы персонала (кафе, менеджер, станки)
    const isPersonnelMode = useMemo(() => {
        return selectedCameras.some(cam => ['personnel_control', 'cafe', 'szsm', 'manager'].includes(cam.operating_mode));
    }, [selectedCameras]);

    const [cameraConfig, setCameraConfig] = useState({});

    // Инициализация конфига при выборе камер
    useEffect(() => {
        setAnalytics(null);
        const initialConfig = {};
        selectedCameras.forEach(cam => {
            const isCamPersonnel = ['personnel_control', 'cafe', 'szsm', 'manager'].includes(cam.operating_mode);
            
            if (isCamPersonnel) {
                initialConfig[cam.id] = {
                    uniqueVehicles: false,
                    maxPeople: true,
                    workTime: true,
                    chairTime: true,
                    phoneTime: true,
                    idleTime: false,
                    downtime: true,
                    efficiency: true
                };
            } else {
                initialConfig[cam.id] = {
                    uniqueVehicles: true,
                    maxPeople: true,
                    workTime: true,
                    chairTime: false,
                    phoneTime: false,
                    idleTime: true,
                    downtime: true,
                    efficiency: true
                };
            }
        });
        setCameraConfig(initialConfig);
    }, [selectedCameras]);

    const handleConfigChange = (camId, metric) => {
        setCameraConfig(prev => ({
            ...prev,
            [camId]: { ...prev[camId], [metric]: !prev[camId][metric] }
        }));
    };

    const toggleAllForMetric = (metric, value) => {
        setCameraConfig(prev => {
            const newConfig = { ...prev };
            Object.keys(newConfig).forEach(camId => {
                newConfig[camId] = { ...newConfig[camId], [metric]: value };
            });
            return newConfig;
        });
    };

    const fetchReports = async () => {
        if (dayjs(endDate).isBefore(dayjs(startDate))) {
            toast.warn(t('common.error'));
            return;
        }
        setLoadingAnalytics(true);
        setAnalytics(null);
        try {
            const token = localStorage.getItem('token');
            const offset = selectedCameras[0]?.timezone_offset || 0;
            
            const reqStartIso = formatToCameraUTC(startDate, offset);
            const reqEndIso = formatToCameraUTC(endDate, offset);

            const payload = {
                camera_ids: selectedCameras.map(c => c.id),
                start_datetime: reqStartIso,
                end_datetime: reqEndIso,
                min_percentage: minPercentage
            };

            const response = await axios.post('/api/multi-camera-analytics/', payload, { headers: { Authorization: `Bearer ${token}` } });
            setAnalytics(response.data);
        } catch (error) {
            toast.error(error.response?.data?.error || t('common.error'));
        } finally {
            setLoadingAnalytics(false);
        }
    };

    const dynamicTotals = useMemo(() => {
        if (!analytics?.by_camera) return null;

        let totalUnique = 0;
        let totalMaxPeople = 0;
        let totalDowntime = 0;
        let totalIdle = 0;
        let totalWork = 0; 
        let totalChair = 0; 
        let totalPhone = 0; 
        let totalWorkAbs = 0; 

        Object.entries(analytics.by_camera).forEach(([camId, stats]) => {
            const config = cameraConfig[camId];
            if (!config) return;

            if (config.uniqueVehicles) totalUnique += (stats.unique_vehicles || 0);
            
            if (config.maxPeople) {
                const currentCamMax = stats.max_concurrent_people || 0;
                if (currentCamMax > totalMaxPeople) totalMaxPeople = currentCamMax;
            }
            
            if (config.downtime) totalDowntime += (stats.total_downtime_seconds || 0);
            if (config.idleTime) totalIdle += (stats.total_idle_time_seconds || 0);
            
            if (config.workTime) {
                totalWork += (stats.total_work_seconds || 0);
                totalWorkAbs += (stats.total_work_absolute_seconds || 0);
            }
            if (config.chairTime) totalChair += (stats.total_chair_seconds || 0);
            if (config.phoneTime) totalPhone += (stats.total_phone_seconds || 0);
        });

        let totalEfficiency = 0;
        if (isPersonnelMode) {
            const useful = Math.max(0, totalWork - totalChair - totalPhone);
            if (totalWork > 0) totalEfficiency = (useful / totalWork) * 100;
        } else {
            if ((totalWorkAbs + totalIdle) > 0) {
                totalEfficiency = (totalWorkAbs / (totalWorkAbs + totalIdle)) * 100;
            }
        }

        return {
            unique_vehicle_count: totalUnique,
            max_concurrent_people: totalMaxPeople,
            total_downtime_seconds: totalDowntime,
            total_idle_time_seconds: totalIdle,
            total_work_seconds: totalWork,
            total_chair_seconds: totalChair,
            total_phone_seconds: totalPhone,
            total_efficiency: totalEfficiency
        };
    }, [analytics, cameraConfig, isPersonnelMode]);
    
    const { pieData, totalDuration } = useMemo(() => {
        if (!dynamicTotals) return { pieData: [], totalDuration: 0 };
        
        const data = [];
        if (dynamicTotals.total_downtime_seconds > 0) {
            const labelKey = isPersonnelMode ? 'camera.downtime_simple' : 'classes.downtime_no_people';
            data.push({ id: 'Downtime', label: t(labelKey), value: dynamicTotals.total_downtime_seconds, color: '#6c757d' });
        }
        
        if (dynamicTotals.total_idle_time_seconds > 0 && !isPersonnelMode) {
            data.push({ id: 'Idle', label: t('classes.idle_vehicle'), value: dynamicTotals.total_idle_time_seconds, color: '#ffc107' });
        }
        
        let displayWorkTime = dynamicTotals.total_work_seconds;
        if (isPersonnelMode) {
            displayWorkTime = Math.max(0, displayWorkTime - dynamicTotals.total_chair_seconds - dynamicTotals.total_phone_seconds);
        }

        if (displayWorkTime > 0) {
            data.push({ id: 'Working', label: t('classes.working'), value: displayWorkTime, color: '#2eb85c' });
        }

        if (isPersonnelMode) {
            if (dynamicTotals.total_chair_seconds > 0) {
                data.push({ id: 'Chair', label: t('multi_cam.col_chair'), value: dynamicTotals.total_chair_seconds, color: '#3399ff' });
            }
            if (dynamicTotals.total_phone_seconds > 0) {
                data.push({ id: 'Phone', label: t('multi_cam.col_phone'), value: dynamicTotals.total_phone_seconds, color: '#ff9933' });
            }
        }
        
        const total = data.reduce((sum, item) => sum + item.value, 0);
        return { pieData: data, totalDuration: total };
    }, [dynamicTotals, t, isPersonnelMode]);

    const cameraTableData = useMemo(() => {
        if (!analytics?.by_camera) return [];
        return Object.entries(analytics.by_camera).map(([id, camStat]) => {
            let efficiency = 0;
            const totalWork = camStat.total_work_seconds || 0; 
            const totalWorkAbs = camStat.total_work_absolute_seconds || 0; 
            const idle = camStat.total_idle_time_seconds || 0;
            const chair = camStat.total_chair_seconds || 0;
            const phone = camStat.total_phone_seconds || 0;

            if (isPersonnelMode) {
                const pureWork = Math.max(0, totalWork - chair - phone);
                if (totalWork > 0) {
                    efficiency = (pureWork / totalWork) * 100;
                }
            } else {
                if ((totalWorkAbs + idle) > 0) {
                    efficiency = (totalWorkAbs / (totalWorkAbs + idle)) * 100;
                }
            }

            return {
                id: parseInt(id),
                name: camStat.name,
                uniqueVehicles: camStat.unique_vehicles || 0,
                workTime: totalWork, 
                chairTime: chair,
                phoneTime: phone,
                idleTime: idle,
                downtime: camStat.total_downtime_seconds,
                maxPeople: camStat.max_concurrent_people || 0,
                efficiency: efficiency
            };
        });
    }, [analytics, isPersonnelMode]);

const handleExportToExcel = async () => {
        if (!analytics) {
            toast.warn(t('common.select_period_msg'));
            return;
        }

        const toastId = toast.loading("Подготовка Excel файла и загрузка скриншотов...");

        try {
            const workbook = new ExcelJS.Workbook();
            const font = { name: 'Arial', size: 11 };
            const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            const headerStyle = { font: { ...font, bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } }, alignment: { vertical: 'middle', horizontal: 'center' }, border };
            const cellStyle = { font, border, alignment: { vertical: 'middle', horizontal: 'left' } };
            const excludedStyle = { font: { ...font, color: { argb: 'FF999999' }, italic: true }, border, alignment: { vertical: 'middle', horizontal: 'center' } };

            let postName = selectedCameras.length === 1 ? selectedCameras[0].name : "Группа_постов";
            const safePostName = postName.replace(/[\\/:*?"<>|]/g, '_');
            const fileName = `Сводный_отчет_${safePostName}_${dayjs(startDate).format('YYYY-MM-DD')}.xlsx`;

            // --- 1. ВКЛАДКА: ОБЩАЯ СВОДКА ---
            const wsSummary = workbook.addWorksheet(t('multi_cam.export_sheet_summary'));
            wsSummary.mergeCells('A1:C1');
            wsSummary.getCell('A1').value = t('multi_cam.export_title');
            wsSummary.getCell('A1').font = { size: 14, bold: true };
            wsSummary.getCell('A2').value = `${t('multi_cam.export_period')} ${dayjs(startDate).format('DD.MM.YYYY HH:mm')} - ${dayjs(endDate).format('DD.MM.YYYY HH:mm')}`;

            const summaryRows = [
                [t('multi_cam.export_param'), t('multi_cam.export_value')],
                [!isPersonnelMode ? `${t('camera.unique_vehicles')} ${t('multi_cam.export_sum_suffix')}` : null, !isPersonnelMode ? dynamicTotals.unique_vehicle_count : null],
                [`${t('camera.work_time')} ${t('multi_cam.export_sum_suffix')}`, formatDuration(dynamicTotals.total_work_seconds)],
                [isPersonnelMode ? `${t('multi_cam.col_chair')} ${t('multi_cam.export_sum_suffix')}` : null, isPersonnelMode ? formatDuration(dynamicTotals.total_chair_seconds) : null],
                [isPersonnelMode ? `${t('multi_cam.col_phone')} ${t('multi_cam.export_sum_suffix')}` : null, isPersonnelMode ? formatDuration(dynamicTotals.total_phone_seconds) : null],
                [`${t('camera.max_people')} ${t('multi_cam.export_max_suffix')}`, dynamicTotals.max_concurrent_people],
                [`Общий КПД`, `${dynamicTotals.total_efficiency.toFixed(1)}%`]
            ].filter(r => r[0] !== null);

            summaryRows.forEach((row, i) => {
                const r = wsSummary.getRow(5 + i);
                r.values = row;
                r.eachCell(c => c.style = i === 0 ? headerStyle : cellStyle);
            });
            wsSummary.columns = [{ width: 40 }, { width: 25 }, { width: 5 }];

            // --- ДОБАВЛЕНИЕ СКРИНШОТОВ ПОСТОВ ---
            const fetchSnapshotAsJpeg = (cam) => {
                return new Promise(async (resolve) => {
                    try {
                        const token = localStorage.getItem('token');
                        const sourceId = cam.parent || cam.id; 
                        
                        const res = await axios.get(`/api/cameras/${cam.id}/snapshot/?source_id=${sourceId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                        });
                        
                        const url = URL.createObjectURL(res.data);
                        const img = new Image();
                        img.onload = () => {
                            const canvas = document.createElement('canvas');
                            canvas.width = 640; 
                            canvas.height = 360;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0, 640, 360);
                            const b64 = canvas.toDataURL('image/jpeg', 0.85); 
                            URL.revokeObjectURL(url);
                            resolve(b64);
                        };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
                        img.src = url;
                    } catch (e) {
                        resolve(null);
                    }
                });
            };

            let currentImgRow = 1; 

            for (const cam of selectedCameras) {
                const b64Jpeg = await fetchSnapshotAsJpeg(cam);
                if (b64Jpeg) {
                    const imageId = workbook.addImage({
                        base64: b64Jpeg,
                        extension: 'jpeg',
                    });
                    
                    const titleCell = wsSummary.getCell(currentImgRow + 1, 4); 
                    titleCell.value = `Снимок: ${cam.name}`;
                    titleCell.font = { bold: true, size: 12 };

                    wsSummary.addImage(imageId, {
                        tl: { col: 3, row: currentImgRow + 1 }, 
                        ext: { width: 480, height: 270 }        
                    });
                    
                    currentImgRow += 16; 
                }
            }

            // --- 2. ВКЛАДКА: ДЕТАЛИЗАЦИЯ ПО ПОСТАМ ---
            const wsCameras = workbook.addWorksheet(t('multi_cam.export_sheet_details'));
            const camHeader = [t('multi_cam.col_post')];
            const metricsOrder = [];
            if (!isPersonnelMode) { camHeader.push(t('multi_cam.col_unique')); metricsOrder.push('uniqueVehicles'); }
            camHeader.push(t('multi_cam.col_max_people')); metricsOrder.push('maxPeople');
            camHeader.push(t('multi_cam.col_work_time')); metricsOrder.push('workTime');
            if (isPersonnelMode) { camHeader.push(t('multi_cam.col_chair'), t('multi_cam.col_phone')); metricsOrder.push('chairTime', 'phoneTime'); }
            camHeader.push(t('multi_cam.col_efficiency')); metricsOrder.push('efficiency');

            wsCameras.addRow(camHeader).eachCell(c => c.style = headerStyle);
            cameraTableData.forEach(cam => {
                const config = cameraConfig[cam.id] || {};
                const rowValues = [cam.name];
                metricsOrder.forEach(m => {
                    let val = cam[m];
                    if (['workTime', 'chairTime', 'phoneTime'].includes(m)) val = formatDuration(cam[m]);
                    if (m === 'efficiency') val = cam[m].toFixed(1) + '%';
                    rowValues.push(config[m] ? val : t('multi_cam.export_excluded'));
                });
                const row = wsCameras.addRow(rowValues);
                row.eachCell((c, idx) => {
                    if (idx === 1) c.style = cellStyle;
                    else c.style = config[metricsOrder[idx-2]] ? cellStyle : excludedStyle;
                });
            });
            wsCameras.columns = camHeader.map((_, i) => ({ width: i === 0 ? 30 : 20 }));

            // --- 3. ВКЛАДКА: ПО ДНЯМ ---
            if (analytics.daily_stats) {
                const wsDaily = workbook.addWorksheet('По дням');
                const dailyHeader = ['Дата', t('multi_cam.col_post'), t('multi_cam.col_work_time'), t('multi_cam.col_max_people')];
                if (isPersonnelMode) {
                    dailyHeader.push(t('multi_cam.col_chair'), t('multi_cam.col_phone'));
                }
                wsDaily.addRow(dailyHeader).eachCell(c => c.style = headerStyle);

                const sortedDates = Object.keys(analytics.daily_stats).sort();
                
                sortedDates.forEach(date => {
                    const dayData = analytics.daily_stats[date];
                    let isFirstRowForDate = true;
                    
                    Object.keys(dayData).forEach(camId => {
                        const st = dayData[camId];
                        const camName = analytics.by_camera[camId]?.name || `Пост ${camId}`;
                        
                        const rowVals = [
                            isFirstRowForDate ? dayjs(date).format('DD.MM.YYYY') : '',
                            camName,
                            formatDuration(st.work_seconds),
                            st.max_people
                        ];
                        
                        if (isPersonnelMode) {
                            rowVals.push(formatDuration(st.chair_seconds), formatDuration(st.phone_seconds));
                        }
                        
                        const row = wsDaily.addRow(rowVals);
                        row.eachCell(c => c.style = cellStyle);
                        isFirstRowForDate = false;
                    });
                    
                    const emptyRow = wsDaily.addRow([]);
                    emptyRow.eachCell(c => c.border = { bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } }});
                });
                
                wsDaily.columns = dailyHeader.map((_, i) => ({ width: i === 0 ? 15 : i === 1 ? 30 : 20 }));
            }

            // --- 4. ВКЛАДКА: ДЕТАЛИЗАЦИЯ ПО ВРЕМЕНИ ---
            const wsTimeline = workbook.addWorksheet("Детализация по времени");
            
            const timelineHeader = ["Пост", "Тип события", "Начало", "Конец", "Длительность", "Состояние / Событие"];
            wsTimeline.addRow(timelineHeader).eachCell(c => c.style = headerStyle);

            Object.entries(analytics.segments_by_camera || {}).forEach(([camId, segments]) => {
                const camData = analytics.by_camera[camId] || {};
                const camMode = camData.operating_mode || 'auto_service';
                const camName = camData.name || `Пост ${camId}`;
                const offset = selectedCameras[0]?.timezone_offset || 0;

                segments.forEach(seg => {
                    const p = seg.people_count || 0;
                    const c_id = seg.class_id;

                    let type = null;
                    let status = null;

                    if (camMode === 'auto_service') {
                        if (c_id === -1) {
                            type = "Статус поста"; status = "Машины нет / Пост пуст";
                        } else if (c_id === -2) {
                            type = "Статус поста"; status = "Машина заехала (людей нет)";
                        } else if (c_id <= -100 && c_id > -300) {
                            type = "Работа"; status = `В работе (${p} чел.)`;
                        }
                    } else {
                        if (c_id === -1) {
                            type = "Статус поста"; status = "Отсутствие сотрудника";
                        } else if (c_id <= -100 && c_id > -300) {
                            type = "Присутствие"; status = `Сотрудник на месте (${p} чел.)`;
                        } else if (c_id <= -300 && c_id > -400) {
                            type = "Нарушение / Стул"; status = `Сидят на стуле (${seg.chair_people_count || p} чел.)`;
                        } else if (c_id <= -400 && c_id > -500) {
                            type = "Нарушение / Телефон"; status = `В телефоне (${seg.phone_people_count || p} чел.)`;
                        } else if (['cafe', 'manager'].includes(camMode) && c_id <= -500 && c_id > -600) {
                            type = "Очередь"; status = `В очереди (${seg.queue_people_count || p} чел.)`;
                        } else if (camMode === 'szsm' && c_id <= -600 && c_id > -700) {
                            type = "Оборудование"; status = `Станок работает (${seg.machine_working_count || p} ед.)`;
                        } else if (camMode === 'manager' && c_id <= -700) {
                            type = "Клиенты"; status = `Общение с клиентом`;
                        }
                    }

                    if (!type || !status) return;

                    const dStart = new Date(seg.start);
                    const start = dayjs(new Date(
                        dStart.getUTCFullYear(), dStart.getUTCMonth(), dStart.getUTCDate(), 
                        dStart.getUTCHours() + 3 + offset, dStart.getUTCMinutes(), dStart.getUTCSeconds()
                    ));

                    const dEnd = new Date(seg.end);
                    const end = dayjs(new Date(
                        dEnd.getUTCFullYear(), dEnd.getUTCMonth(), dEnd.getUTCDate(), 
                        dEnd.getUTCHours() + 3 + offset, dEnd.getUTCMinutes(), dEnd.getUTCSeconds()
                    ));

                    const row = wsTimeline.addRow([
                        camName,
                        type,
                        start.format('HH:mm:ss'),
                        end.format('HH:mm:ss'),
                        formatDuration(end.diff(start, 'second')),
                        status
                    ]);
                    row.eachCell(c => c.style = cellStyle);
                });
                wsTimeline.addRow([]); 
            });

            wsTimeline.columns = [
                { width: 15 }, 
                { width: 22 }, 
                { width: 12 }, 
                { width: 12 }, 
                { width: 15 }, 
                { width: 40 }  
            ];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, fileName);

            toast.update(toastId, { render: "Отчет успешно скачан!", type: "success", isLoading: false, autoClose: 3000 });
        } catch (error) {
            console.error(error);
            toast.update(toastId, { render: "Произошла ошибка при экспорте.", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const hintStyle = { borderBottom: '1px dashed #999', cursor: 'help', width: 'fit-content' };

    return (
        <CCol md={12} className="mt-4">
            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <span>{t('multi_cam.summary_title', { count: selectedCameras.length })}</span>
                    {analytics && !loadingAnalytics && (
                        <div className="d-flex gap-2">
                            <CButton color="secondary" variant={showConfig ? "outline" : "solid"} size="sm" onClick={() => setShowConfig(!showConfig)}>
                                <CIcon icon={cilSettings} className="me-2"/>
                                {showConfig ? t('multi_cam.btn_hide_config') : t('multi_cam.btn_show_config')}
                            </CButton>
                            <CButton color="success" onClick={handleExportToExcel} className="text-white" size="sm">
                                {t('common.export_excel')}
                            </CButton>
                        </div>
                    )}
                </CCardHeader>
                <CCardBody>
                    <CRow className="align-items-end mb-4">
                        <CCol md={3} className="mb-2 mb-md-0"><label>{t('common.date_start')}</label><DatePicker selected={startDate} onChange={(date) => setStartDate(date)} selectsStart startDate={startDate} endDate={endDate} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></CCol>
                        <CCol md={3} className="mb-2 mb-md-0"><label>{t('common.date_end')}</label><DatePicker selected={endDate} onChange={(date) => setEndDate(date)} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} showTimeSelect className="form-control" dateFormat="dd.MM.yyyy HH:mm" locale={i18n.language === 'ru' ? 'ru' : 'en'} /></CCol>
                        <CCol md={4} className="mb-2 mb-md-0">
                            <CTooltip content={t("tooltips.min_percentage")}>
                                <label style={hintStyle}>{t('multi_cam.min_percentage_label')}</label>
                            </CTooltip>
                            <CInputGroup>
                                <CFormInput type="number" min="0" max="100" value={minPercentage} onChange={(e) => setMinPercentage(e.target.value)} />
                                <span className="input-group-text">%</span>
                            </CInputGroup>
                        </CCol>
                        <CCol md={2}><CButton color="primary" onClick={fetchReports} disabled={loadingAnalytics} className="w-100">{loadingAnalytics ? <CSpinner size="sm"/> : t('common.get_report')}</CButton></CCol>
                    </CRow>

                    {loadingAnalytics && <div className="text-center p-3"><CSpinner/></div>}
                    
                    {analytics && !loadingAnalytics && dynamicTotals && (
                        <>
                            <CCollapse visible={showConfig}>
                                <div className="p-3 mb-4 border rounded bg-light">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="m-0">{t('multi_cam.config_title')}</h6>
                                        <div className="small text-muted">{t('multi_cam.config_subtitle')}</div>
                                    </div>
                                    <div className="table-responsive" style={{maxHeight: '300px'}}>
                                        <CTable small bordered hover className="bg-white mb-0">
                                            <CTableHead color="light" style={{position: 'sticky', top: 0}}>
                                                <CTableRow>
                                                    <CTableHeaderCell>{t('multi_cam.col_post')}</CTableHeaderCell>
                                                    {!isPersonnelMode && (
                                                        <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                            <div className="d-flex flex-column align-items-center">
                                                                <CTooltip content={t("tooltips.unique_vehicles")}><span style={hintStyle}>{t('multi_cam.col_unique')}</span></CTooltip>
                                                                <div className="mt-1">
                                                                    <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('uniqueVehicles', true)}/></CTooltip>
                                                                    <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('uniqueVehicles', false)}/></CTooltip>
                                                                </div>
                                                            </div>
                                                        </CTableHeaderCell>
                                                    )}
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <CTooltip content={t("tooltips.max_people")}><span style={hintStyle}>{t('multi_cam.col_max_people')}</span></CTooltip>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('maxPeople', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('maxPeople', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <CTooltip content={t("tooltips.total_work")}><span style={hintStyle}>{t('multi_cam.col_work_time')}</span></CTooltip>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('workTime', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('workTime', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    {isPersonnelMode && (
                                                        <>
                                                            <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <CTooltip content={t("tooltips.chair")}><span style={hintStyle}>{t('multi_cam.col_chair')}</span></CTooltip>
                                                                    <div className="mt-1">
                                                                        <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('chairTime', true)}/></CTooltip>
                                                                        <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('chairTime', false)}/></CTooltip>
                                                                    </div>
                                                                </div>
                                                            </CTableHeaderCell>
                                                            <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <CTooltip content={t("tooltips.phone")}><span style={hintStyle}>{t('multi_cam.col_phone')}</span></CTooltip>
                                                                    <div className="mt-1">
                                                                        <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('phoneTime', true)}/></CTooltip>
                                                                        <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('phoneTime', false)}/></CTooltip>
                                                                    </div>
                                                                </div>
                                                            </CTableHeaderCell>
                                                        </>
                                                    )}
                                                    {!isPersonnelMode && (
                                                        <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                            <div className="d-flex flex-column align-items-center">
                                                                <CTooltip content={t("tooltips.idle")}><span style={hintStyle}>{t('multi_cam.col_idle')}</span></CTooltip>
                                                                <div className="mt-1">
                                                                    <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('idleTime', true)}/></CTooltip>
                                                                    <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('idleTime', false)}/></CTooltip>
                                                                </div>
                                                            </div>
                                                        </CTableHeaderCell>
                                                    )}
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '100px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <CTooltip content={t("tooltips.downtime")}><span style={hintStyle}>{t('multi_cam.col_downtime')}</span></CTooltip>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('downtime', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('downtime', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell className="text-center" style={{minWidth: '80px'}}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <CTooltip content={t("tooltips.efficiency_activity")}><span style={hintStyle}>{t('multi_cam.col_efficiency')}</span></CTooltip>
                                                            <div className="mt-1">
                                                                <CTooltip content={t('multi_cam.tooltip_enable_all')}><CIcon icon={cilCheckCircle} size="sm" className="text-success cursor-pointer me-2" onClick={() => toggleAllForMetric('efficiency', true)}/></CTooltip>
                                                                <CTooltip content={t('multi_cam.tooltip_disable_all')}><CIcon icon={cilXCircle} size="sm" className="text-danger cursor-pointer" onClick={() => toggleAllForMetric('efficiency', false)}/></CTooltip>
                                                            </div>
                                                        </div>
                                                    </CTableHeaderCell>
                                                </CTableRow>
                                            </CTableHead>
                                            <CTableBody>
                                                {cameraTableData.map(cam => (
                                                    <CTableRow key={cam.id}>
                                                        <CTableDataCell><strong>{cam.name}</strong></CTableDataCell>
                                                        {!isPersonnelMode && <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.uniqueVehicles} onChange={() => handleConfigChange(cam.id, 'uniqueVehicles')}/></CTableDataCell>}
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.maxPeople} onChange={() => handleConfigChange(cam.id, 'maxPeople')}/></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.workTime} onChange={() => handleConfigChange(cam.id, 'workTime')}/></CTableDataCell>
                                                        {isPersonnelMode && (
                                                            <>
                                                                <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.chairTime} onChange={() => handleConfigChange(cam.id, 'chairTime')}/></CTableDataCell>
                                                                <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.phoneTime} onChange={() => handleConfigChange(cam.id, 'phoneTime')}/></CTableDataCell>
                                                            </>
                                                        )}
                                                        {!isPersonnelMode && <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.idleTime} onChange={() => handleConfigChange(cam.id, 'idleTime')}/></CTableDataCell>}
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.downtime} onChange={() => handleConfigChange(cam.id, 'downtime')}/></CTableDataCell>
                                                        <CTableDataCell className="text-center"><CFormCheck checked={cameraConfig[cam.id]?.efficiency} onChange={() => handleConfigChange(cam.id, 'efficiency')}/></CTableDataCell>
                                                    </CTableRow>
                                                ))}
                                            </CTableBody>
                                        </CTable>
                                    </div>
                                </div>
                            </CCollapse>

                            <CRow>
                                <CCol md={6}>
                                    <h5 className="mb-3">{t('multi_cam.general_summary')} <small className="text-muted">{t('multi_cam.summary_dynamic')}</small></h5>
                                    <CListGroup flush className="mb-3">
                                        {!isPersonnelMode && (
                                            <CListGroupItem>
                                                <CTooltip content={t("tooltips.unique_vehicles")}>
                                                    <strong style={hintStyle}>{t('camera.unique_vehicles')}:</strong>
                                                </CTooltip> {dynamicTotals.unique_vehicle_count}
                                            </CListGroupItem>
                                        )}
                                        <CListGroupItem>
                                            <CTooltip content={t("tooltips.max_people")}>
                                                <strong style={hintStyle}>{t('camera.max_people')}:</strong>
                                            </CTooltip> {dynamicTotals.max_concurrent_people}
                                            <span className="text-muted ms-2 small">({'>'} {minPercentage}% от времени работы)</span>
                                        </CListGroupItem>
                                        
                                        <CListGroupItem>
                                            <CTooltip content={t("tooltips.downtime")}>
                                                <strong style={hintStyle}>{t(isPersonnelMode ? 'camera.downtime_simple' : 'camera.total_downtime')}:</strong>
                                            </CTooltip> {formatDuration(dynamicTotals.total_downtime_seconds)}
                                        </CListGroupItem>
                                        
                                        {!isPersonnelMode && (
                                            <CListGroupItem>
                                                <CTooltip content={t("tooltips.idle")}>
                                                    <strong style={hintStyle}>{t('camera.total_idle')}:</strong>
                                                </CTooltip> {formatDuration(dynamicTotals.total_idle_time_seconds)}
                                            </CListGroupItem>
                                        )}
                                        
                                        <CListGroupItem>
                                            <CTooltip content={t("tooltips.total_work")}>
                                                <strong style={hintStyle}>{t('camera.work_time')}:</strong>
                                            </CTooltip> {formatDuration(dynamicTotals.total_work_seconds)}
                                            <small className="ms-2 text-muted fst-italic">({t('multi_cam.man_hours', {hours: ''})})</small>
                                        </CListGroupItem>
                                        
                                        {isPersonnelMode && (
                                            <>
                                                <CListGroupItem>
                                                    <CTooltip content={t("tooltips.chair")}>
                                                        <strong style={hintStyle}>{t('multi_cam.col_chair')}:</strong>
                                                    </CTooltip> {formatDuration(dynamicTotals.total_chair_seconds)}
                                                </CListGroupItem>
                                                <CListGroupItem>
                                                    <CTooltip content={t("tooltips.phone")}>
                                                        <strong style={hintStyle}>{t('multi_cam.col_phone')}:</strong>
                                                    </CTooltip> {formatDuration(dynamicTotals.total_phone_seconds)}
                                                </CListGroupItem>
                                            </>
                                        )}
                                        <CListGroupItem>
                                            <CTooltip content={t("tooltips.efficiency_activity")}>
                                                <strong style={hintStyle}>Общий КПД:</strong> 
                                            </CTooltip>
                                            <span className={`ms-2 fw-bold ${dynamicTotals.total_efficiency > 50 ? 'text-success' : 'text-danger'}`}>
                                                {dynamicTotals.total_efficiency.toFixed(1)}%
                                            </span>
                                        </CListGroupItem>
                                    </CListGroup>
                                </CCol>
                                <CCol md={6}>
                                    <div style={{ height: '300px' }}>
                                        {pieData.length > 0 ? (
                                            <ResponsivePie
                                                data={pieData}
                                                margin={{ top: 20, right: 140, bottom: 20, left: 20 }}
                                                innerRadius={0.6}
                                                padAngle={1}
                                                cornerRadius={3}
                                                activeOuterRadiusOffset={8}
                                                colors={{ datum: 'data.color' }}
                                                enableArcLinkLabels={false}
                                                arcLabelsSkipAngle={15}
                                                arcLabelsTextColor="#ffffff"
                                                arcLabel={d => totalDuration > 0 ? `${((d.value / totalDuration) * 100).toFixed(0)}%` : ''}
                                                tooltip={({ datum }) => (
                                                    <div style={{ background: 'white', padding: '5px 10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                                                        <div style={{ width: '10px', height: '10px', background: datum.color, display: 'inline-block', marginRight: '5px' }}></div>
                                                        <strong>{datum.label}</strong>: {formatDuration(datum.value)}
                                                    </div>
                                                )}
                                                legends={[{
                                                    anchor: 'right', direction: 'column', justify: false, translateX: 120, translateY: 0,
                                                    itemsSpacing: 10, itemWidth: 100, itemHeight: 18, itemTextColor: '#999',
                                                    symbolSize: 14, symbolShape: 'circle', effects: [{ on: 'hover', style: { itemTextColor: '#000' } }]
                                                }]}
                                            />
                                        ) : <p className="text-center text-muted pt-5">Нет данных для графика (проверьте настройки)</p>}
                                    </div>
                                </CCol>
                            </CRow>
                            
                            <h5 className="mt-4 mb-3">{t('multi_cam.stats_by_posts')}</h5>
                            <div className="table-responsive">
                                <CTable hover bordered>
                                    <CTableHead color="light">
                                        <CTableRow>
                                            <CTableHeaderCell>{t('multi_cam.col_post')}</CTableHeaderCell>
                                            {!isPersonnelMode && (
                                                <CTableHeaderCell>
                                                    <CTooltip content={t("tooltips.unique_vehicles")}><span style={hintStyle}>{t('multi_cam.col_unique')}</span></CTooltip>
                                                </CTableHeaderCell>
                                            )}
                                            <CTableHeaderCell>
                                                <CTooltip content={t("tooltips.max_people")}><span style={hintStyle}>{t('multi_cam.col_max_people')}</span></CTooltip>
                                            </CTableHeaderCell>
                                            <CTableHeaderCell>
                                                <CTooltip content={t("tooltips.total_work")}><span style={hintStyle}>{t('multi_cam.col_work_time')} (ч-ч)</span></CTooltip>
                                            </CTableHeaderCell>
                                            {isPersonnelMode && (
                                                <>
                                                    <CTableHeaderCell>
                                                        <CTooltip content={t("tooltips.chair")}><span style={hintStyle}>{t('multi_cam.col_chair')}</span></CTooltip>
                                                    </CTableHeaderCell>
                                                    <CTableHeaderCell>
                                                        <CTooltip content={t("tooltips.phone")}><span style={hintStyle}>{t('multi_cam.col_phone')}</span></CTooltip>
                                                    </CTableHeaderCell>
                                                </>
                                            )}
                                            {!isPersonnelMode && (
                                                <CTableHeaderCell>
                                                    <CTooltip content={t("tooltips.idle")}><span style={hintStyle}>{t('multi_cam.col_idle')}</span></CTooltip>
                                                </CTableHeaderCell>
                                            )}
                                            <CTableHeaderCell>
                                                <CTooltip content={t("tooltips.downtime")}><span style={hintStyle}>{t('multi_cam.col_downtime')}</span></CTooltip>
                                            </CTableHeaderCell>
                                            <CTableHeaderCell>
                                                <CTooltip content={t("tooltips.efficiency_activity")}><span style={hintStyle}>{t('multi_cam.col_efficiency')}</span></CTooltip>
                                            </CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {cameraTableData.map((cam, idx) => {
                                            const config = cameraConfig[cam.id] || {};
                                            const mutedStyle = { color: '#adb5bd', textDecoration: 'line-through' };
                                            return (
                                                <CTableRow key={idx}>
                                                    <CTableDataCell><strong>{cam.name}</strong></CTableDataCell>
                                                    {!isPersonnelMode && <CTableDataCell style={!config.uniqueVehicles ? mutedStyle : {}}>{cam.uniqueVehicles}</CTableDataCell>}
                                                    <CTableDataCell style={!config.maxPeople ? mutedStyle : {}}>{cam.maxPeople}</CTableDataCell>
                                                    <CTableDataCell style={!config.workTime ? mutedStyle : {}} className={config.workTime ? "text-success" : ""}>{formatDuration(cam.workTime)}</CTableDataCell>
                                                    
                                                    {isPersonnelMode && (
                                                        <>
                                                            <CTableDataCell style={!config.chairTime ? mutedStyle : {}} className={config.chairTime ? "text-info" : ""}>{formatDuration(cam.chairTime)}</CTableDataCell>
                                                            <CTableDataCell style={!config.phoneTime ? mutedStyle : {}} className={config.phoneTime ? "text-warning" : ""}>{formatDuration(cam.phoneTime)}</CTableDataCell>
                                                        </>
                                                    )}

                                                    {!isPersonnelMode && <CTableDataCell style={!config.idleTime ? mutedStyle : {}} className={config.idleTime ? "text-warning" : ""}>{formatDuration(cam.idleTime)}</CTableDataCell>}
                                                    <CTableDataCell style={!config.downtime ? mutedStyle : {}} className={config.downtime ? "text-secondary" : ""}>{formatDuration(cam.downtime)}</CTableDataCell>
                                                    <CTableDataCell style={!config.efficiency ? mutedStyle : {}}><strong>{cam.efficiency.toFixed(1)}%</strong></CTableDataCell>
                                                </CTableRow>
                                            );
                                        })}
                                    </CTableBody>
                                </CTable>
                                <p className="text-muted small">{t('multi_cam.excluded_note')}</p>
                            </div>
                        </>
                    )}
                </CCardBody>
            </CCard>
        </CCol>
    );
};

export default MultiCameraAnalytics;